import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import type { TransactionHash } from "@/types";

/**
 * Prepares an increase unwinding epochs transaction
 */
export const prepareIncreaseUnwindingEpochsTransaction = async (
    oldUnwindingEpochs: number,
    newUnwindingEpochs: number,
    shares: bigint,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "increaseUnwindingEpochs",
            args: [oldUnwindingEpochs, newUnwindingEpochs, shares],
        }),
    };
};

/**
 * Estimates gas for an increase unwinding epochs transaction
 */
export const estimateIncreaseUnwindingEpochsTransactionGas = async (
    oldUnwindingEpochs: number,
    newUnwindingEpochs: number,
    shares: bigint,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareIncreaseUnwindingEpochsTransaction(
        oldUnwindingEpochs,
        newUnwindingEpochs,
        shares,
        walletClient,
    );
    return estimateContractTransactionGas(walletClient, txRequest);
};

/**
 * Executes an increase unwinding epochs transaction
 */
export const gatewayIncreaseUnwindingEpochsTransaction = async (
    oldUnwindingEpochs: number,
    newUnwindingEpochs: number,
    shares: bigint,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareIncreaseUnwindingEpochsTransaction(
        oldUnwindingEpochs,
        newUnwindingEpochs,
        shares,
        walletClient,
    );

    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};
